// Lock de auth PACIENTE — o P1 da issue #461.
//
// ─────────────────────────────────────────────────────────────────────────
// O DEFEITO QUE ISTO MATA (lido do @supabase/auth-js 2.99.3 instalado)
// ─────────────────────────────────────────────────────────────────────────
// Toda chamada `supabase.from()/.rpc()/functions.invoke()` serializa a leitura
// de sessão num Web Lock. O GoTrueClient usa `lockAcquireTimeout` DEFAULT de
// 5000ms (GoTrueClient.js:28) em TODOS os call sites. Quando um waiter estoura
// os 5s, o navigatorLock "recupera" roubando o lock com `{ steal: true }`
// (locks.js:210) — e o request desse steal NÃO tem catch próprio
// (locks.js:205-228): se um SEGUNDO waiter estourar e roubar do primeiro
// ladrão, o AbortError cru ("Lock broken by another request with the 'steal'
// option") sobe até o caller. Na rajada de montagem do /mirror os timers dos
// N waiters disparam quase juntos — a cascata é o caso comum, não o raro.
// É o VYV-FRONTEND-3Q (174 ocorrências) e a irmã VYV-FRONTEND-4.
//
// Por que ELEVAR o timeout é o conserto certo AQUI:
//   · O steal existe para recuperar lock ÓRFÃO — o comentário do próprio
//     auth-js cita o double-mount do React Strict Mode. Este app NÃO usa
//     StrictMode (src/main.tsx), e a spec do Web Locks libera o lock quando a
//     página morre. Lock verdadeiramente órfão, em produção, praticamente não
//     existe aqui.
//   · O holder legítimo mais lento é o refresh de token segurando o lock
//     durante o POST /token — numa rede de WebView em cold start isso passa
//     de 5s com facilidade. Roubar de um holder LENTO é exatamente o erro.
//   · 30s cobre o pior refresh realista + a drenagem da fila da montagem, e
//     ainda recupera em 30s no caso patológico de um fetch pendurado (que a
//     pilha de rede do SO mata sozinha bem antes de "para sempre").
//
// TRADE-OFF, declarado: se um lock REALMENTE travar, todas as chamadas
// dependentes de auth esperam até 30s antes do steal de recuperação — contra
// 5s hoje. Aceito: o caso é raro-a-inexistente neste app, e o custo do steal
// prematuro é falha IMEDIATA de chamadas em cascata, todo dia, na superfície
// mais lenta.
//
// POR QUE VIA OPÇÃO `lock` E NÃO `lockAcquireTimeout`: o supabase-js 2.99.3
// desestrutura as opções de auth SELETIVAMENTE em `_initSupabaseAuthClient`
// ({ autoRefreshToken, persistSession, …, lock, debug }) — `lockAcquireTimeout`
// NÃO está na lista e morreria em silêncio. `lock` está. Então o timeout entra
// por um wrapper do próprio `navigatorLock` do auth-js.
//
// Módulo puro de propósito (sem tocar no client) para o vitest provar as
// regras sem precisar de env de Supabase.

export const AUTH_LOCK_MIN_ACQUIRE_TIMEOUT_MS = 30_000;

/**
 * Eleva SÓ timeouts positivos.
 *
 * `0` significa "não espere" (`ifAvailable: true` no navigatorLock) e negativo
 * significa "espere para sempre" — os dois são contratos do CHAMADOR, e
 * transformar `0` em 30s inverteria o sentido ("não espere" virando "espere
 * meio minuto"). Só o caso "espere até N ms" é paciência nossa de dar.
 */
export function patientAcquireTimeout(requested: number): number {
  if (!Number.isFinite(requested) || requested <= 0) return requested;
  return Math.max(requested, AUTH_LOCK_MIN_ACQUIRE_TIMEOUT_MS);
}

export type AuthLockFn = <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
) => Promise<R>;

/** O `navigatorLock` do auth-js com a paciência aplicada por cima. */
export function makePatientLock(base: AuthLockFn): AuthLockFn {
  return (name, acquireTimeout, fn) =>
    base(name, patientAcquireTimeout(acquireTimeout), fn);
}
